import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { waitForSpinnerMfe } from './initSpinnerMfe';
import { datadogRum } from '@datadog/browser-rum';

/*
  Embeds the existing Carvana 360° spinner MFE (`SpinnerLoaderModule`) in
  damage-marking mode. Ported from the spinner-standalone POC's SpinnerMfe.tsx,
  adapted to this app's CDN env + MFE framework (already initialized via MfeConfig
  in _app). Loads frames by stockNumber from public blob storage.

  VERSION: our feature reaches into the spinner MFE's undocumented internals (its Redux
  store, window globals, DOM data-qa attributes — see spinnerMfeBridge), and the MFE is
  separately deployed. We always load whatever build is currently published, and record
  its hash via `sar-spinner-version` so drift is still observable in Datadog even though
  it no longer blocks the flow.
*/

// Fastly CDN base (spinner JS) + public blob storage (vehicle 360 data). Prod and test live on
// different hosts (carvana.io vs carvanatech.com); pick by env so production never pulls spinner
// data from the test host (which 404s for prod vehicles and silently falls back to the tile list).
const IS_PROD = process.env.NODE_ENV === 'production';
const CDN_URL =
  process.env.CARVANA_APP_CDN_URL_FASTLY ||
  (IS_PROD ? 'https://assets.fastly.carvana.io' : 'https://assets.fastly.carvanatech.com');
const BLOB_URL = IS_PROD ? 'https://spinnerdata.carvana.io' : 'https://spinnerdata.carvanatech.com';

function cacheBuster() {
  const date = new Date();
  const quarterHour = Math.floor(date.getMinutes() / 15) * 15;
  return `${date.getDate()}-${date.getHours()}-${quarterHour}`;
}

// Extract the content hash from a spinner index URL (…/index.<hash>.js).
function spinnerHashFromManifest(indexUrl: string): string | null {
  return indexUrl.match(/index\.([a-z0-9]+)\.js/i)?.[1] ?? null;
}

type SpinnerMfeProps = {
  stockNumber: number;
  onReady?: () => void;
  // MFE bundle failed to load — the caller falls back to the classic tile list.
  onFailed?: () => void;
};

const SpinnerMfeComponent = ({ stockNumber, onReady, onFailed }: SpinnerMfeProps) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const onFailedRef = React.useRef(onFailed);
  onFailedRef.current = onFailed;
  const onReadyRef = React.useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    let cancelled = false;
    const manifest = `${CDN_URL}/spinner/spinner-loader/assets-manifest.json?v=${cacheBuster()}`;

    const bail = () => {
      if (!cancelled) {
        setFailed(true);
        onFailedRef.current?.();
      }
    };

    (async () => {
      // Record which spinner build is live (for drift visibility in Datadog) but never
      // block on it — we always load whatever is currently published.
      try {
        const res = await fetch(manifest, { cache: 'no-store' });
        const json = await res.json();
        const hash = spinnerHashFromManifest(String(json?.['index.js'] ?? ''));
        if (cancelled) return;
        datadogRum.addAction('sar-spinner-version', { hash });
      } catch (err) {
        datadogRum.addAction('sar-spinner-version', {
          verified: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
      if (cancelled) return;

      try {
        // Wait for the SAR MFE's own microfrontend instance to be configured before loading
        // the spinner. This is kicked off once from mfe-module.tsx (the MFE entry point) so
        // it runs ONLY when these components are loaded as the ServiceAndRepairModule MFE
        // (e.g. the Vite my-cars host). When the OP app renders these components from its own
        // bundle, mfe-module.tsx never runs, so waitForSpinnerMfe() resolves immediately and
        // we do NOT reconfigure the OP app's already-configured framework instance.
        await waitForSpinnerMfe();
        if (cancelled) return;

        // useQueueForInitialMfeLoad:false — this is a nested MfeLoad (an MFE loading an
        // MFE). In a Vite host (e.g. the new my-cars app) the SAR MFE gets its OWN
        // @carvana/microfrontend instance rather than sharing the host's (as it does under
        // Webpack/Module Federation), so on that fresh instance isFirstRun/!isMferLoaded are
        // true. The default initial-load queue then swallows this nested call and never
        // drains it, so the spinner-loader silently never loads and we fall back to the
        // tile list. Opting out of the queue makes the nested load run immediately.
        // No-op under Webpack hosts, where isMferLoaded is already true here.
        await MfeLoad(
          {
            files: [['SpinnerLoaderModule', [{ component: 'default', default: { file: 'index.js', manifest } }]]],
          },
          { useQueueForInitialMfeLoad: false }
        );
        if (!cancelled) setLoaded(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('SpinnerLoaderModule failed to load:', err);
        bail();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Stable config/listener so re-renders driven by the parent's zoom/camera state (which
  // change dozens of times/sec during a pinch) never hand MfeReactComponent new prop
  // identities and reconcile the heavy spinner subtree. Reads onReady via a ref so the
  // memoization doesn't depend on the caller passing a stable callback.
  const eventListener = useCallback((event: { type: string }) => {
    if (event?.type === 'ui/setSpinnerReady' || event?.type === 'spinner/ui/SET_SPINNER_READY') {
      onReadyRef.current?.();
    }
  }, []);
  const spinnerConfig = useMemo(
    () => ({
      autoResize: true,
      autoResizing: true,
      didLoadExperiments: true,
      isNewVehicle: false,
      callbacks: { firstSpinCB: () => {} },
      eventListener,
    }),
    [eventListener]
  );

  if (failed) {
    // Caller (SpinnerLocation) falls back to the granular list on failure.
    return null;
  }
  if (!loaded) {
    return <div data-testid="spinner-loading">Loading your vehicle…</div>;
  }

  return (
    <MfeReactComponent
      module="SpinnerLoaderModule"
      component="default"
      stockNumber={stockNumber}
      baseBlobUrl={BLOB_URL}
      sourceURL={CDN_URL}
      spinnerConfig={spinnerConfig}
    />
  );
};

// Renders identically for a fixed stockNumber, so memo it out of the parent's high-frequency
// zoom/camera render path.
export const SpinnerMfe = React.memo(SpinnerMfeComponent);

export default SpinnerMfe;
